import type { Transaction } from "@rocicorp/zero";
import {
  type Context,
  NOT_FOUND_MESSAGES,
  userFacingError,
} from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { assertAccess } from "../rbac/access";
import type { Permission } from "../rbac/permissions";

export type StringKey<T> = Extract<keyof T, string>;

export type IdField<TArgs, Optional extends boolean | undefined> = StringKey<{
  [K in keyof TArgs as TArgs[K] extends (
    Optional extends true ? string | null | undefined : string
  )
    ? K
    : never]: unknown;
}>;

type NullableFetchScope = ReturnType<typeof _fetchScope>;

export type AwaitedScopeRow = {
  [K in keyof NullableFetchScope]: NonNullable<
    Awaited<ReturnType<NullableFetchScope[K]>>
  >;
};

export type ResourceKind = keyof NullableFetchScope;

export type AnyRbacResourceLookup =
  | {
      kind: ResourceKind;
      from: string;
      optional?: boolean;
      swallowError?: boolean;
    }
  | {
      kind: ResourceKind;
      from: string;
      many: true;
    }
  | {
      kind: ResourceKind;
      from: string;
      field: string;
    };

/**
 * `Omit<T, K>` is not distributive over unions — it collapses discriminated
 * unions into their common subset. This variant distributes so e.g. a SAML
 * vs OIDC SSO schema keeps both branches after stripping resolved id keys.
 */
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

type ArrayIdField<TArgs> = StringKey<{
  [K in keyof TArgs as TArgs[K] extends readonly string[] ? K : never]: unknown;
}>;

type ObjectArrayField<TArgs> = StringKey<{
  [K in keyof TArgs as TArgs[K] extends readonly Record<string, unknown>[]
    ? K
    : never]: unknown;
}>;

export type ResourceLookup<TArgs> =
  | {
      kind: ResourceKind;
      from: IdField<TArgs, false>;
      optional?: false;
      swallowError?: boolean;
    }
  | {
      kind: ResourceKind;
      from: IdField<TArgs, true>;
      optional: true;
      swallowError?: boolean;
    }
  | {
      kind: ResourceKind;
      from: ArrayIdField<TArgs>;
      many: true;
    }
  | {
      kind: ResourceKind;
      from: ObjectArrayField<TArgs>;
      field: string;
    };

export type DeclarativeResolve<TArgs> = Record<
  string,
  ResourceLookup<TArgs & object>
>;

export type DeclResult<T> = {
  [K in keyof T]: T[K] extends {
    kind: infer Kind extends keyof AwaitedScopeRow;
    field: string;
  }
    ? AwaitedScopeRow[Kind][]
    : T[K] extends {
          kind: infer Kind extends keyof AwaitedScopeRow;
          many: true;
        }
      ? AwaitedScopeRow[Kind][]
      : T[K] extends {
            kind: infer Kind extends keyof AwaitedScopeRow;
            optional: true;
          }
        ? AwaitedScopeRow[Kind] | null
        : T[K] extends {
              kind: infer Kind extends keyof AwaitedScopeRow;
              swallowError: true;
            }
          ? AwaitedScopeRow[Kind] | null
          : T[K] extends { kind: infer Kind extends keyof AwaitedScopeRow }
            ? AwaitedScopeRow[Kind]
            : never;
};

export type StrippedFromKeys<T> = {
  [K in keyof T]: T[K] extends { optional: true }
    ? never
    : T[K] extends { field: string }
      ? never
      : T[K] extends { from: infer F extends string }
        ? F
        : never;
}[keyof T];

export type FnArgs<TOutput, TDecl> = DistributiveOmit<
  TOutput,
  StrippedFromKeys<TDecl>
> &
  DeclResult<TDecl>;

// Server-mutator variant: raw id keys are kept because the body forwards
// the original args to the shared mutator's fn, which re-resolves them.
export type UnstrippedFnArgs<TOutput, TDecl> = TOutput & DeclResult<TDecl>;

// ---------------------------------------------------------------------------
// Runtime helpers.
// ---------------------------------------------------------------------------

export function collectStrippedFromKeys(
  decl: Record<string, AnyRbacResourceLookup>
): Set<string> {
  const out = new Set<string>();
  for (const spec of Object.values(decl)) {
    if ("optional" in spec && spec.optional) continue;
    if ("field" in spec) continue;
    out.add(spec.from);
  }
  return out;
}

/**
 * Construction-time guard. Run once when `mutator()` / `serverMutator()`
 * is invoked at module load. Catches the footgun where a resolve key
 * matches a `from` field — the resolved row would overwrite the raw id,
 * then get stripped, and the body would see neither.
 */
export function validateResolveConfig(
  decl: Record<string, AnyRbacResourceLookup>,
  kind: "mutator" | "serverMutator"
): void {
  const fromFields = new Set<string>();
  for (const spec of Object.values(decl)) {
    fromFields.add(spec.from);
  }
  for (const key of Object.keys(decl)) {
    if (fromFields.has(key)) {
      throw new Error(
        `${kind}(): resolve key "${key}" collides with a 'from' field; the resolved value would overwrite the raw id and then be stripped, leaving the body without either. Pick a different key (e.g. "${key}Resolved" or drop the "Id" suffix on the from field).`
      );
    }
  }
}

function _fetchScope(tx: Pick<Transaction<Schema>, "run">) {
  return {
    space: async (id: string) => {
      const [r] = await tx.run(zql.spaces.where("id", id));
      return r ?? null;
    },
    workflow: async (id: string) => {
      const [r] = await tx.run(
        zql.workflows.where("id", id).where("deletedAt", "IS", null)
      );
      return r ?? null;
    },
    deletedWorkflow: async (id: string) => {
      const [r] = await tx.run(
        zql.workflows.where("id", id).where("deletedAt", "IS NOT", null)
      );
      return r ?? null;
    },
    branch: async (id: string) => {
      const [r] = await tx.run(
        zql.workflowBranches.where("id", id).related("workflow")
      );
      if (!r?.workflow) return null;
      return { ...r, workflow: r.workflow };
    },
    commit: async (id: string) => {
      const [r] = await tx.run(
        zql.workflowCommits.where("id", id).related("branch")
      );
      if (!r?.branch) return null;
      return { ...r, branch: r.branch };
    },
    run: async (id: string) => {
      const [r] = await tx.run(zql.workflowRuns.where("id", id));
      return r ?? null;
    },
    connectedApp: async (id: string) => {
      const [r] = await tx.run(zql.connectedApps.where("id", id));
      return r ?? null;
    },
    skill: async (id: string) => {
      const [r] = await tx.run(zql.skills.where("id", id));
      return r ?? null;
    },
    aiProvider: async (id: string) => {
      const [r] = await tx.run(zql.aiProviders.where("id", id));
      return r ?? null;
    },
    oauthConnection: async (id: string) => {
      const [r] = await tx.run(
        zql.oauthConnections.where("id", id).related("connectedApps")
      );
      return r ?? null;
    },
    chat: async (id: string) => {
      const [r] = await tx.run(zql.chats.where("id", id));
      return r ?? null;
    },
    attachment: async (id: string) => {
      const [r] = await tx.run(zql.attachments.where("id", id));
      return r ?? null;
    },
    ssoProvider: async (id: string) => {
      const [r] = await tx.run(zql.ssoProvider.where("id", id));
      return r ?? null;
    },
    user: async (id: string) => {
      const [r] = await tx.run(zql.user.where("id", id));
      return r ?? null;
    },
    userGroup: async (id: string) => {
      const [r] = await tx.run(zql.userGroups.where("id", id));
      return r ?? null;
    },
    serviceAccount: async (id: string) => {
      const [r] = await tx.run(zql.serviceAccounts.where("id", id));
      return r ?? null;
    },
    serviceAccountFederation: async (id: string) => {
      const [r] = await tx.run(zql.serviceAccountFederations.where("id", id));
      return r ?? null;
    },
    oidcProvider: async (id: string) => {
      const [r] = await tx.run(zql.oidcProviders.where("id", id));
      return r ?? null;
    },
    oauthClient: async (id: string) => {
      const [r] = await tx.run(zql.oauthClient.where("id", id));
      return r ?? null;
    },
    oauthClientServiceAccount: async (id: string) => {
      const [r] = await tx.run(zql.oauthClientServiceAccount.where("id", id));
      return r ?? null;
    },
    oauthGrant: async (id: string) => {
      const [r] = await tx.run(zql.oauthGrant.where("id", id));
      return r ?? null;
    },
    role: async (id: string) => {
      const [r] = await tx.run(zql.roles.where("id", id));
      return r ?? null;
    },
    connectionClaim: async (id: string) => {
      const [r] = await tx.run(
        zql.connectionClaims.where("id", id).related("workflow")
      );
      if (!r?.workflow) return null;
      return { ...r, workflow: r.workflow };
    },
    grantRequest: async (id: string) => {
      const [r] = await tx.run(zql.grantRequests.where("id", id));
      return r ?? null;
    },
    spaceVolume: async (id: string) => {
      const [r] = await tx.run(zql.spaceVolumes.where("id", id));
      return r ?? null;
    },
    network: async (id: string) => {
      const [r] = await tx.run(zql.networks.where("id", id));
      return r ?? null;
    },
    egressRoute: async (id: string) => {
      const [r] = await tx.run(
        zql.egressRoutes.where("id", id).related("network")
      );
      if (!r?.network) return null;
      return { ...r, network: r.network };
    },
    egressRule: async (id: string) => {
      const [r] = await tx.run(zql.egressRules.where("id", id));
      return r ?? null;
    },
    auditLogDestination: async (id: string) => {
      const [r] = await tx.run(zql.auditLogDestinations.where("id", id));
      return r ?? null;
    },
    featureFlag: async (id: string) => {
      const [r] = await tx.run(zql.featureFlags.where("id", id));
      return r ?? null;
    },
    aiBudget: async (id: string) => {
      const [r] = await tx.run(zql.aiBudgets.where("id", id));
      return r ?? null;
    },
    connectorUsageBudget: async (id: string) => {
      const [r] = await tx.run(zql.connectorUsageBudgets.where("id", id));
      return r ?? null;
    },
    connectorUsagePrice: async (id: string) => {
      const [r] = await tx.run(zql.connectorUsagePrices.where("id", id));
      return r ?? null;
    },
  };
}

// A resolved row belonging to another tenant is indistinguishable from one that
// doesn't exist: the id lookup is unscoped (no row-level security yet), so this
// is the boundary that keeps one tenant from acting on another's row by id.
function belongsToTenant(row: unknown, tenantId: string): boolean {
  const rowTenantId = (row as { tenantId?: unknown }).tenantId;
  if (typeof rowTenantId !== "string") return true;
  return rowTenantId === tenantId;
}

export async function resolve(
  decl: Record<string, AnyRbacResourceLookup>,
  opts: {
    args: Record<string, unknown>;
    tx: Pick<Transaction<Schema>, "run">;
    tenantId: string;
  }
): Promise<Record<string, unknown>> {
  if (Object.keys(decl).length === 0) return {};

  const scope = _fetchScope(opts.tx);
  const fetch = async (kind: ResourceKind, id: string) => {
    const row = await scope[kind](id);
    if (row == null || !belongsToTenant(row, opts.tenantId)) return null;
    return row;
  };
  const entries = await Promise.all(
    Object.entries(decl).map(async ([resultKey, spec]) => {
      if ("many" in spec && spec.many) {
        const ids = opts.args[spec.from] as string[];
        const rows = await Promise.all(
          ids.map(async (id) => {
            const row = await fetch(spec.kind, id);
            if (row == null)
              throw userFacingError(NOT_FOUND_MESSAGES[spec.kind]);
            return row;
          })
        );
        return [resultKey, rows] as const;
      }

      if ("field" in spec && spec.field) {
        const arr = opts.args[spec.from] as Record<string, unknown>[];
        const rows = (
          await Promise.all(
            arr.map(async (item) => {
              const id = item[spec.field] as string | undefined;
              if (id == null) return null;
              const row = await fetch(spec.kind, id);
              if (row == null)
                throw userFacingError(NOT_FOUND_MESSAGES[spec.kind]);
              return row;
            })
          )
        ).filter((r) => r != null);
        return [resultKey, rows] as const;
      }

      const idValue = opts.args[spec.from] as string | null | undefined;
      if (idValue == null) {
        if ("optional" in spec && spec.optional)
          return [resultKey, null] as const;
        throw new Error(`Missing ${spec.kind} id at args.${spec.from}`);
      }
      const value = await fetch(spec.kind, idValue);
      if (value == null) {
        if ("swallowError" in spec && spec.swallowError)
          return [resultKey, null] as const;
        throw userFacingError(NOT_FOUND_MESSAGES[spec.kind]);
      }
      return [resultKey, value] as const;
    })
  );
  return Object.fromEntries(entries);
}

// ---------------------------------------------------------------------------
// Mutation execution — used by both mutator.ts and serverMutator.ts.
// ---------------------------------------------------------------------------

export type PermissionRequirement<TArgs> = {
  permission:
    | Permission
    | ((opts: { args: TArgs; ctx: Context }) => Permission);
  // Returning null skips this requirement — only for requirements whose
  // target resource is optional; the fn must handle the absent case itself.
  scopeId: (opts: { args: TArgs; ctx: Context }) => string | null;
};

export type PermissionsConfig<TArgs> = readonly [
  PermissionRequirement<TArgs>,
  ...PermissionRequirement<TArgs>[],
];

/**
 * Every mutator states its access control here, with no default: either a
 * non-empty list of permission requirements checked before the body runs,
 * or an explicit `{ none }` with the reason access is intentionally open
 * (e.g. the body only touches rows owned by `ctx.userId`).
 */
export type PermissionsDeclaration<TArgs> =
  | PermissionsConfig<TArgs>
  | { readonly none: string };

/**
 * Construction-time normalization. Runs once at module load, so a
 * malformed declaration (anything that isn't a non-empty requirements
 * array or `{ none: "reason" }`) fails fast instead of silently running
 * with no checks.
 */
export function permissionRequirements<TArgs>(
  declaration: PermissionsDeclaration<TArgs>,
  kind: "mutator" | "serverMutator"
): readonly PermissionRequirement<TArgs>[] {
  if (Array.isArray(declaration)) {
    if (declaration.length > 0) return declaration;
  } else if (
    typeof declaration === "object" &&
    declaration !== null &&
    "none" in declaration &&
    typeof declaration.none === "string" &&
    declaration.none.length > 0
  ) {
    return [];
  }
  throw new Error(
    `${kind}(): permissions must be a non-empty requirements array or { none: "reason" }`
  );
}

export async function runMutation<
  TTx extends Pick<Transaction<Schema>, "run">,
>(d: {
  tx: TTx;
  ctx: Context;
  args: Record<string, unknown>;
  decl: Record<string, AnyRbacResourceLookup>;
  stripped: Set<string>;
  permissions: readonly PermissionRequirement<Record<string, unknown>>[];
  fn: (opts: {
    tx: TTx;
    ctx: Context;
    args: Record<string, unknown>;
  }) => Promise<void>;
}): Promise<void> {
  const fetched = await resolve(d.decl, {
    args: d.args,
    tx: d.tx,
    tenantId: d.ctx.tenantId,
  });
  const merged: Record<string, unknown> = { ...d.args, ...fetched };
  for (const k of d.stripped) delete merged[k];

  try {
    await Promise.all(
      d.permissions.map(async (p) => {
        const scopeId = p.scopeId({ args: merged, ctx: d.ctx });
        if (scopeId === null) return;
        await assertAccess({
          tx: d.tx,
          userId: d.ctx.userId,
          principal: d.ctx.principal,
          tenantId: d.ctx.tenantId,
          permission:
            typeof p.permission === "function"
              ? p.permission({ args: merged, ctx: d.ctx })
              : p.permission,
          scopeId,
        });
      })
    );
  } catch (err) {
    if (!(err instanceof Error) || err.message !== "Access denied") throw err;
    throw userFacingError("Not found");
  }

  await d.fn({ tx: d.tx, ctx: d.ctx, args: merged });
}
